import Grid from "@mui/material/Grid"
import { BacktestStore } from "../../redux/reducers/backtesting/backtestingReducer"
import { useEffect, useState } from "react"
import { BackTestApi } from "../../api/BackTestApi"
import Card from "@mui/material/Card"
import Avatar from "@mui/material/Avatar"
import { SchedulePieChart } from "./SchedulePieChart"
import moment from "moment"
import { amber, blue, green, orange, purple, red } from "@mui/material/colors"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import Box from "@mui/material/Box"
import { alpha, useTheme } from "@mui/material/styles"
import { dashboardMutedTextSx, getDashboardPanelSx } from "../dashboardv2/dashboardStyles"

export const UserBacktestingStatsTab = () => {
  const theme = useTheme()

    const [ gettingTests, setGettingTests ] = useState(false)
  
    const [ tests, setTests ] = useState<BacktestStore[]>([])

    useEffect(() => {
  
      getAllUserBacktestConfigs(true)
  
    }, [])

  const getAllUserBacktestConfigs = async (progress: boolean) => {

    if(progress) setGettingTests(true)
    try
    {
      const response = await new BackTestApi().getAllForUsers()
      if(response) setTests(response)
    }
    catch(error)
    {

    }

    if(progress) setGettingTests(false)

  }

  const calcStats = () => {

    let notifyAfterComplete = 0
    let enabledConfigs = 0
    let inQueue = 0
    let pending10Mins = 0
    let pendingHour = 0
    let pending4Hours = 0
    let schedule4h = 0;
    let schedule8h = 0;
    let schedule12h = 0;
    let schedule24h = 0;
    let schedule48h = 0;
    let scheduleWeek = 0;
    let allAverageRunDuration = 0;

    if(tests)
    {
      for(const config of tests)
      {
        if(config.isEnabled) enabledConfigs++
        if(config.nextRun < new Date().toISOString()) inQueue++
        if(config.nextRun > new Date().toISOString() && config.nextRun < moment().add(10, "minutes").toISOString()) pending10Mins++
        if(config.nextRun > new Date().toISOString() && config.nextRun < moment().add(1, "hour").toISOString()) pendingHour++
        if(config.nextRun > new Date().toISOString() && config.nextRun < moment().add(4, "hour").toISOString()) pending4Hours++

        if(config.notifyAfterComplete) notifyAfterComplete++
        if(config.schedule === 4) schedule4h++
        if(config.schedule === 8) schedule8h++
        if(config.schedule === 12) schedule12h++
        if(config.schedule === 24) schedule24h++
        if(config.schedule === 48) schedule48h++
        if(config.schedule === 168) scheduleWeek++
        if(config.averageRunDuration) allAverageRunDuration = allAverageRunDuration + (config.averageRunDuration || 0)
      }
    }

    return { averageRunDuration: calcAverageRunDuration(allAverageRunDuration, tests), totalConfigs: tests.length, enabledConfigs, inQueue, notifyAfterComplete, pending4Hours, pendingHour, pending10Mins, schedule4h, schedule8h, schedule12h, schedule24h, schedule48h, scheduleWeek }
  }

  const calcAverageRunDuration = (allRuns: number, tests: BacktestStore[]) => {
    const testsWithRunDuration = tests.filter(t => t.averageRunDuration > 0)
    return allRuns / testsWithRunDuration.length
  }

  const formatDuration = (seconds: number) => {
    const duration = moment.duration(seconds, 'seconds');
    return `${Math.floor(duration.asMinutes())} minutes ${duration.seconds()} seconds`;
}

  const { averageRunDuration, totalConfigs, enabledConfigs, inQueue, notifyAfterComplete, pending10Mins, pending4Hours, pendingHour, schedule4h, schedule8h, schedule12h, schedule24h, schedule48h, scheduleWeek } = calcStats()


  return (
    <Box sx={{ ...getDashboardPanelSx(theme), p: { xs: 2, md: 2.5 } }}>
    <Typography variant="overline" sx={{ letterSpacing: 1.5, ...dashboardMutedTextSx(theme) }}>
      User Config Stats
    </Typography>
    <Typography sx={{ fontSize: 28, lineHeight: 1.15, mb: 2.5 }}>
      Queue health, scheduling pressure, and notification coverage
    </Typography>
    <Grid container spacing={3}>

    <Grid item container direction="row" spacing={3}>
      <Grid item xs={6}>
          <Card sx={{ ...getDashboardPanelSx(theme, 300), p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 1.4, ...dashboardMutedTextSx(theme) }}>
                  Queue
                </Typography>
                <Typography sx={{ fontSize: 34, mt: 1 }}>Backtests in Queue</Typography>
                <Typography sx={{ mt: 1, ...dashboardMutedTextSx(theme) }}>
                  Average run duration: {formatDuration(averageRunDuration)}
                </Typography>
              </Box>
              <Avatar style={{ fontSize: 64 }} sx={{ width: 160, height: 160, bgcolor: alpha(theme.palette.info.main, 0.2), color: theme.palette.text.primary }}>
                {inQueue}
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={6}>
          <Card sx={{ ...getDashboardPanelSx(theme, 300), p: 1.5 }}>
            <SchedulePieChart scheduleWeek={scheduleWeek} schedule4h={schedule4h} schedule8h={schedule8h} schedule48h={schedule48h} schedule12h={schedule12h} schedule24h={schedule24h} />
          </Card>
        </Grid>

      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        <Card sx={{ ...styles.statCard(theme) }}>
          <Avatar sx={{ width: 84, height: 84, bgcolor: amber[400] }}>{totalConfigs}</Avatar>
          <Typography sx={{ mt: 2, fontSize: 22 }}>Total configs</Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        <Card sx={{ ...styles.statCard(theme) }}>
          <Avatar sx={{ width: 84, height: 84, bgcolor: blue[400] }}>{enabledConfigs}</Avatar>
          <Typography sx={{ mt: 2, fontSize: 22 }}>Enabled Configs</Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        <Card sx={{ ...styles.statCard(theme) }}>
          <Avatar sx={{ width: 84, height: 84, bgcolor: green[400] }}>{notifyAfterComplete}</Avatar>
          <Typography sx={{ mt: 2, fontSize: 22 }}>Notifications Enabled</Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        <Card sx={{ ...styles.statCard(theme) }}>
          <Avatar sx={{ width: 84, height: 84, bgcolor: red[400] }}>{pending10Mins}</Avatar>
          <Typography sx={{ mt: 2, fontSize: 22 }}>Pending Run</Typography>
          <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>In the next 10 minutes</Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        <Card sx={{ ...styles.statCard(theme) }}>
          <Avatar sx={{ width: 84, height: 84, bgcolor: orange[400] }}>{pendingHour}</Avatar>
          <Typography sx={{ mt: 2, fontSize: 22 }}>Pending Run</Typography>
          <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>In the next hour</Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        <Card sx={{ ...styles.statCard(theme) }}>
          <Avatar sx={{ width: 84, height: 84, bgcolor: purple[400] }} >{pending4Hours}</Avatar>
          <Typography sx={{ mt: 2, fontSize: 22 }}>Pending Run</Typography>
          <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>In the next 4 hours</Typography>
        </Card>
      </Grid>

    </Grid>
    </Box>
  )
}

const styles = {
  statCard: (theme: any) => ({
    ...getDashboardPanelSx(theme),
    minHeight: 220,
    p: 2.5,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
  }),
}
